import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { api } from '../App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { resolveImageUrl, resolveThumbUrl } from '../utils/horseHelpers';
import { toast } from 'sonner';
import { ShoppingCart, Coins, Package, Home, TreePine, X, Minus, Plus, CheckCircle } from 'lucide-react';

// Category configuration for Mercantile
const CATEGORY_CONFIG = {
  supplies: { label: 'Supplies', icon: Package, color: '#6FCFD3' },
  tack: { label: 'Tack', icon: ShoppingCart, color: '#D66238' },
  animals: { label: 'Horses', icon: Package, color: '#8B4513' },
  structures: { label: 'Structures', icon: Home, color: '#4A90D9' },
  landscaping: { label: 'Landscaping', icon: TreePine, color: '#228B22' }
};

const Ranch = ({ user, logout }) => {
  const navigate = useNavigate();
  
  // Admin-only access check
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Ranch is currently in development. Admin access only.');
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  const [blocks, setBlocks] = useState([]);
  const [mustangs, setMustangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mustangsLoading, setMustangsLoading] = useState(true);
  const [activeView, setActiveView] = useState('game'); // Always show game view
  const [showAddHorseDialog, setShowAddHorseDialog] = useState(false);
  const [showComingSoonDialog, setShowComingSoonDialog] = useState(false);
  const [horseForm, setHorseForm] = useState({
    name: '',
    age: '',
    gender: '',
    color: '',
    hma: '',
    height: '',
    disposition: '',
    photo_url: ''
  });
  const scriptRefs = useRef([]);
  const styleRefs = useRef([]);
  
  // Mercantile state
  const [showMercantile, setShowMercantile] = useState(false);
  const [mercantileItems, setMercantileItems] = useState([]);
  const [userCoins, setUserCoins] = useState(0);
  const [mercantileLoading, setMercantileLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('supplies');
  const [selectedItem, setSelectedItem] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchBlocks();
    fetchMustangs();
  }, []);

  // Send mustang data to iframe - always send even if empty (to confirm data loaded)
  const sendDataToIframe = useCallback(() => {
    const iframe = document.getElementById('ranchGameIframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'RANCH_USER_DATA',
        user: user,
        mustangs: mustangs
      }, '*');
      console.log('📤 Sent user data to ranch game iframe:', { user: user?.email, mustangCount: mustangs.length });
    }
  }, [user, mustangs]);

  // Send updated mustang data to iframe when mustangs load
  useEffect(() => {
    if (!mustangsLoading) {
      sendDataToIframe();
    }
  }, [mustangsLoading, sendDataToIframe]);

  // Listen for messages from iframe (data requests, save/load)
  useEffect(() => {
    const handleMessage = async (event) => {
      if (!event.data || !event.data.type) return;
      
      const iframe = document.getElementById('ranchGameIframe');
      
      if (event.data.type === 'REQUEST_RANCH_DATA') {
        console.log('📥 Received data request from iframe');
        sendDataToIframe();
      }
      
      // Proxy save request from iframe
      if (event.data.type === 'SAVE_RANCH_LAYOUT') {
        console.log('📥 Received save request from iframe');
        try {
          const response = await api.post('/ranch/game/save-layout', { objects: event.data.objects });
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'SAVE_RANCH_RESULT',
              success: true,
              message: 'Layout saved'
            }, '*');
          }
          console.log('✅ Saved ranch layout via parent');
        } catch (error) {
          console.error('Error saving ranch layout:', error);
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'SAVE_RANCH_RESULT',
              success: false,
              error: error.message
            }, '*');
          }
        }
      }
      
      // Proxy load request from iframe
      if (event.data.type === 'LOAD_RANCH_LAYOUT') {
        console.log('📥 Received load request from iframe');
        try {
          const response = await api.get('/ranch/game/load-layout');
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'LOAD_RANCH_RESULT',
              success: true,
              objects: response.data.objects || []
            }, '*');
          }
          console.log('✅ Loaded ranch layout via parent:', response.data.objects?.length || 0, 'objects');
        } catch (error) {
          console.error('Error loading ranch layout:', error);
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'LOAD_RANCH_RESULT',
              success: false,
              objects: [],
              error: error.message
            }, '*');
          }
        }
      }
      
      // Proxy use inventory item request from iframe
      if (event.data.type === 'USE_INVENTORY_ITEM') {
        console.log('📥 Received use inventory request from iframe:', event.data.itemKey);
        try {
          await api.post('/ranch/build-inventory/use', { 
            item_key: event.data.itemKey, 
            quantity: 1 
          });
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'USE_INVENTORY_RESULT',
              success: true,
              messageId: event.data.messageId
            }, '*');
          }
          console.log('✅ Used inventory item via parent:', event.data.itemKey);
        } catch (error) {
          console.error('Error using inventory item:', error);
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'USE_INVENTORY_RESULT',
              success: false,
              messageId: event.data.messageId,
              error: error.message
            }, '*');
          }
        }
      }
      
      // Proxy return inventory item request from iframe
      if (event.data.type === 'RETURN_INVENTORY_ITEM') {
        console.log('📥 Received return inventory request from iframe:', event.data.itemKey);
        try {
          await api.post('/ranch/build-inventory/return', { 
            item_key: event.data.itemKey, 
            quantity: 1 
          });
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'RETURN_INVENTORY_RESULT',
              success: true,
              messageId: event.data.messageId
            }, '*');
          }
          console.log('✅ Returned inventory item via parent:', event.data.itemKey);
        } catch (error) {
          console.error('Error returning inventory item:', error);
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'RETURN_INVENTORY_RESULT',
              success: false,
              messageId: event.data.messageId,
              error: error.message
            }, '*');
          }
        }
      }
      
      // Proxy load inventory request from iframe
      if (event.data.type === 'LOAD_INVENTORY') {
        console.log('📥 Received load inventory request from iframe');
        try {
          const response = await api.get('/ranch/build-inventory');
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'LOAD_INVENTORY_RESULT',
              success: true,
              items: response.data.items || {}
            }, '*');
          }
          console.log('✅ Loaded inventory via parent:', Object.keys(response.data.items || {}).length, 'items');
        } catch (error) {
          console.error('Error loading inventory:', error);
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'LOAD_INVENTORY_RESULT',
              success: false,
              items: {},
              error: error.message
            }, '*');
          }
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendDataToIframe]);

  const fetchBlocks = async () => {
    try {
      const response = await api.get('/ranch-blocks');
      setBlocks(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ranch blocks:', error);
      setLoading(false);
    }
  };

  const fetchMustangs = async () => {
    try {
      const response = await api.get('/user-mustangs');
      setMustangs(response.data);
      setMustangsLoading(false);
    } catch (error) {
      console.error('Error fetching mustangs:', error);
      setMustangsLoading(false);
    }
  };

  // Mercantile functions
  const fetchMercantileItems = async () => {
    setMercantileLoading(true);
    try {
      const response = await api.get('/mercantile/items');
      setMercantileItems(response.data.items || []);
      setUserCoins(response.data.user_coins || 0);
    } catch (error) {
      console.error('Error fetching mercantile items:', error);
      toast.error('Failed to load store items');
    } finally {
      setMercantileLoading(false);
    }
  };

  const openMercantile = () => {
    setShowMercantile(true);
    fetchMercantileItems();
  };

  const handlePurchase = async () => {
    if (!selectedItem) return;
    setPurchasing(true);
    try {
      const response = await api.post('/mercantile/purchase', {
        item_id: selectedItem.id,
        quantity: purchaseQuantity
      });
      toast.success(response.data.message);
      setUserCoins(response.data.new_balance);
      setSelectedItem(null);
      setPurchaseQuantity(1);
      fetchMercantileItems();
    } catch (error) {
      console.error('Error purchasing item:', error);
      toast.error(error.response?.data?.detail || 'Failed to purchase item');
    } finally {
      setPurchasing(false);
    }
  };

  const getItemsByCategory = (category) => {
    return mercantileItems.filter(item => item.category === category);
  };

  const canAfford = (item) => {
    return userCoins >= item.price * purchaseQuantity;
  };

  // Listen for mercantile open event from ranch game iframe
  useEffect(() => {
    const handleMercantileOpen = () => {
      setShowMercantile(true);
      // Fetch items when opening
      api.get('/mercantile/items').then(response => {
        setMercantileItems(response.data.items || []);
        setUserCoins(response.data.user_coins || 0);
      }).catch(error => {
        console.error('Error fetching mercantile items:', error);
      });
    };
    
    const handleMessage = (event) => {
      if (event.data === 'openMercantile') {
        handleMercantileOpen();
      }
    };
    window.addEventListener('message', handleMessage);
    
    // Also expose function globally for the iframe
    window.openMercantileModal = handleMercantileOpen;
    
    return () => {
      window.removeEventListener('message', handleMessage);
      delete window.openMercantileModal;
    };
  }, []);

  const handleAddHorse = async (e) => {
    e.preventDefault();
    try {
      await api.post('/user-mustangs/add-real', {
        ...horseForm,
        is_real_adoption: true
      });
      
      // Reset form
      setHorseForm({
        name: '',
        age: '',
        gender: '',
        color: '',
        hma: '',
        height: '',
        disposition: '',
        photo_url: ''
      });
      
      // Close dialog
      setShowAddHorseDialog(false);
      
      // Refresh list
      fetchMustangs();
      
      alert('Horse added successfully!');
    } catch (error) {
      console.error('Error adding horse:', error);
      alert('Failed to add horse. Please try again.');
    }
  };

  const handleDeleteHorse = async (mustangId, mustangName) => {
    if (!window.confirm(`Are you sure you want to remove ${mustangName} from your ranch?`)) {
      return;
    }

    try {
      await api.delete(`/user-mustangs/${mustangId}`);
      
      // Refresh list
      fetchMustangs();
      
      alert(`${mustangName} has been removed from your ranch.`);
    } catch (error) {
      console.error('Error deleting horse:', error);
      alert('Failed to delete horse. Please try again.');
    }
  };

  // Inject CSS and JavaScript for each block
  useEffect(() => {
    // Clean up previous styles
    styleRefs.current.forEach(style => {
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    });
    styleRefs.current = [];

    // Clean up previous scripts
    scriptRefs.current.forEach(script => {
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });
    scriptRefs.current = [];

    // Inject CSS styles
    blocks.forEach((block, index) => {
      if (block.css_content) {
        const style = document.createElement('style');
        style.textContent = block.css_content;
        style.id = `ranch-block-style-${index}`;
        document.head.appendChild(style);
        styleRefs.current.push(style);
      }
    });

    // Execute JavaScript
    blocks.forEach((block, index) => {
      if (block.js_content) {
        const script = document.createElement('script');
        script.text = block.js_content;
        script.id = `ranch-block-script-${index}`;
        document.body.appendChild(script);
        scriptRefs.current.push(script);
      }
    });

    return () => {
      // Cleanup on unmount
      styleRefs.current.forEach(style => {
        if (style && style.parentNode) {
          style.parentNode.removeChild(style);
        }
      });
      scriptRefs.current.forEach(script => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [blocks]);

  const blocksByPosition = {
    top: blocks.filter(b => b.position === 'top'),
    main: blocks.filter(b => b.position === 'main'),
    sidebar: blocks.filter(b => b.position === 'sidebar'),
    bottom: blocks.filter(b => b.position === 'bottom'),
  };

  if (loading || mustangsLoading) {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
        <Navigation user={user} logout={logout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{color: '#D66238'}}>
          My Ranch
        </h1>

        {/* List View - Hidden */}
        {false && (
          <div>
            {/* Section 1: My Horses List */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <h2 className="text-2xl sm:text-3xl font-bold" style={{color: '#D66238'}}>
              My Horses
            </h2>
            <Button
              onClick={() => setShowAddHorseDialog(true)}
              className="text-white font-semibold"
              style={{backgroundColor: '#6FCFD3'}}
            >
              <span className="mr-2">➕</span>
              Add Horse
            </Button>
          </div>
          
          {mustangs.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-8xl mb-4">🐴</div>
              <p className="text-xl mb-4" style={{color: '#D66238'}}>
                You haven't adopted any mustangs yet!
              </p>
              <p className="text-gray-600 mb-6">
                Visit the Virtual Corral to adopt your first mustang
              </p>
              <button
                onClick={() => window.location.href = '/virtual-corral'}
                className="px-8 py-3 rounded-full text-white font-semibold"
                style={{backgroundColor: '#6FCFD3'}}
              >
                Visit Virtual Corral
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mustangs.map((mustang) => (
                <div
                  key={mustang.id}
                  className="border-2 rounded-xl p-4 hover:shadow-lg transition-all"
                  style={{borderColor: mustang.is_real_adoption ? '#FFD700' : '#6FCFD3'}}
                >
                  {/* Delete Button */}
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => handleDeleteHorse(mustang.id, mustang.name)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-all"
                      title="Remove horse"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  {/* Horse Photo */}
                  {mustang.photo_url && (
                    <div className="mb-4 rounded-lg overflow-hidden" style={{aspectRatio: '1/1'}}>
                      <img
                        src={resolveThumbUrl(mustang.photo_url)}
                        alt={mustang.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          if (e.target.src.includes('/thumb/')) {
                            e.target.src = resolveImageUrl(mustang.photo_url);
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Horse Info */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold" style={{color: '#D66238'}}>
                        {mustang.name}
                      </h3>
                      {mustang.is_real_adoption && (
                        <span className="text-2xl" title="Real Mustang">⭐</span>
                      )}
                    </div>

                    <div className="text-sm text-gray-600 space-y-1">
                      <p><span className="font-semibold">Age:</span> {mustang.age || 'Unknown'}</p>
                      <p><span className="font-semibold">Gender:</span> {mustang.gender || 'Unknown'}</p>
                      <p><span className="font-semibold">Color:</span> {mustang.color || 'Unknown'}</p>
                      {mustang.hma && (
                        <p><span className="font-semibold">HMA:</span> {mustang.hma}</p>
                      )}
                      {mustang.adoption_method && (
                        <p>
                          <span className="font-semibold">Adopted via:</span>{' '}
                          <span className="px-2 py-0.5 rounded text-xs" style={{backgroundColor: '#6FCFD3', color: 'white'}}>
                            {mustang.adoption_method}
                          </span>
                        </p>
                      )}
                    </div>

                    {/* Type Badge */}
                    <div className="pt-2">
                      <span
                        className="inline-block px-3 py-1 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: mustang.is_real_adoption ? '#FFD700' : '#6FCFD3',
                          color: mustang.is_real_adoption ? '#8B4513' : 'white'
                        }}
                      >
                        {mustang.is_real_adoption ? '⭐ Real Mustang' : '🎮 Virtual Mustang'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </div>
        )}

        {/* Game View - Always Visible */}
        {(
          <div>
            {/* Isometric Ranch Game - Always Visible */}
            <div id="ranchGameContainer" className="mb-8">
              <iframe 
                id="ranchGameIframe"
                src={`/ranch-game.html?v=${Date.now()}`}
                style={{
                  width: '100%', 
                  height: '1000px', 
                  border: 'none',
                  borderRadius: '20px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  overflow: 'auto'
                }}
                scrolling="auto"
                title="Isometric Ranch Game"
                onLoad={() => {
                  // Send user data to iframe once it loads
                  console.log('📤 Iframe loaded, sending data. Mustangs loading:', mustangsLoading);
                  sendDataToIframe();
                }}
              />
            </div>

            {/* Additional Admin Blocks (if any) */}
            {blocks.length > 0 && (
              <>
                {/* Top Blocks */}
                {blocksByPosition.top.length > 0 && (
                  <div className="mb-8 space-y-4">
                    {blocksByPosition.top.map((block) => (
                      <div key={block.id} dangerouslySetInnerHTML={{ __html: block.html_content }} />
                    ))}
                  </div>
                )}

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Main Blocks */}
                  <div className="lg:col-span-2 space-y-4">
                    {blocksByPosition.main.length > 0 && (
                      blocksByPosition.main.map((block) => (
                        <div key={block.id} dangerouslySetInnerHTML={{ __html: block.html_content }} />
                      ))
                    )}
                  </div>

                  {/* Sidebar Blocks */}
                  {blocksByPosition.sidebar.length > 0 && (
                    <div className="space-y-4">
                      {blocksByPosition.sidebar.map((block) => (
                        <div key={block.id} dangerouslySetInnerHTML={{ __html: block.html_content }} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Bottom Blocks */}
                {blocksByPosition.bottom.length > 0 && (
                  <div className="mt-8 space-y-4">
                    {blocksByPosition.bottom.map((block) => (
                      <div key={block.id} dangerouslySetInnerHTML={{ __html: block.html_content }} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Add Horse Dialog */}
        <Dialog open={showAddHorseDialog} onOpenChange={setShowAddHorseDialog}>
          <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{color: '#D66238'}}>Add New Horse</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleAddHorse} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Horse Name *</label>
                <input
                  type="text"
                  required
                  value={horseForm.name}
                  onChange={(e) => setHorseForm({...horseForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{focusRing: '#6FCFD3'}}
                  placeholder="Spirit"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Age</label>
                  <input
                    type="text"
                    value={horseForm.age}
                    onChange={(e) => setHorseForm({...horseForm, age: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="5 years"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Gender</label>
                  <select
                    value={horseForm.gender}
                    onChange={(e) => setHorseForm({...horseForm, gender: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  >
                    <option value="">Select gender</option>
                    <option value="Mare">Mare</option>
                    <option value="Stallion">Stallion</option>
                    <option value="Gelding">Gelding</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Color</label>
                  <input
                    type="text"
                    value={horseForm.color}
                    onChange={(e) => setHorseForm({...horseForm, color: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Bay, Chestnut, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Height</label>
                  <input
                    type="text"
                    value={horseForm.height}
                    onChange={(e) => setHorseForm({...horseForm, height: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="14.2 hands"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">HMA (Herd Management Area)</label>
                <input
                  type="text"
                  value={horseForm.hma}
                  onChange={(e) => setHorseForm({...horseForm, hma: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  placeholder="McCullough Peaks"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Disposition</label>
                <input
                  type="text"
                  value={horseForm.disposition}
                  onChange={(e) => setHorseForm({...horseForm, disposition: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  placeholder="Gentle, Spirited, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Photo URL</label>
                <input
                  type="url"
                  value={horseForm.photo_url}
                  onChange={(e) => setHorseForm({...horseForm, photo_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  placeholder="https://example.com/horse-photo.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">Enter a URL to a horse photo</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddHorseDialog(false)}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:w-auto text-white"
                  style={{backgroundColor: '#6FCFD3'}}
                >
                  Add Horse
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Coming Soon Dialog */}
        <Dialog open={showComingSoonDialog} onOpenChange={setShowComingSoonDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-center" style={{color: '#D66238'}}>
                🏗️ Coming Soon!
              </DialogTitle>
            </DialogHeader>
            <div className="py-6 text-center">
              <div className="flex justify-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ranch-simulator-1/artifacts/v08qtzue_horse_transparent_stylized.png" 
                  alt="Horse"
                  className="w-24 h-24 object-contain"
                />
              </div>
              <p className="text-lg text-gray-700 mb-4">
                The Ranch Game is currently under development and will be available soon!
              </p>
              <p className="text-sm text-gray-600">
                In the meantime, you can continue managing your horses in the "My Horses" list.
              </p>
            </div>
            <div className="flex justify-center">
              <Button
                onClick={() => setShowComingSoonDialog(false)}
                className="px-6 py-2 text-white font-semibold"
                style={{backgroundColor: '#6FCFD3'}}
              >
                Got it!
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Mercantile Modal */}
        {showMercantile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              style={{ border: '4px solid #D66238' }}
            >
              {/* Header */}
              <div className="p-4 flex items-center justify-between" style={{ backgroundColor: '#D66238' }}>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">🏪</span>
                  <h2 className="text-2xl font-bold text-white">Mercantile</h2>
                </div>
                <div className="flex items-center gap-4">
                  {/* Coin Balance */}
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: '#FFF8E1' }}>
                    <Coins className="w-5 h-5" style={{ color: '#FFD700' }} />
                    <span className="font-bold" style={{ color: '#D66238' }}>{userCoins.toLocaleString()}</span>
                  </div>
                  {/* Close Button */}
                  <button
                    onClick={() => { setShowMercantile(false); setSelectedItem(null); }}
                    className="p-2 rounded-full hover:bg-white/20 transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>

              {mercantileLoading ? (
                <div className="flex-1 flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#6FCFD3' }}></div>
                </div>
              ) : selectedItem ? (
                /* Purchase View */
                <div className="flex-1 overflow-y-auto p-6">
                  <button 
                    onClick={() => { setSelectedItem(null); setPurchaseQuantity(1); }}
                    className="mb-4 text-sm flex items-center gap-1 hover:underline"
                    style={{ color: '#6FCFD3' }}
                  >
                    ← Back to items
                  </button>
                  
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Item Image */}
                    <div className="w-full md:w-1/3">
                      <div 
                        className="aspect-square rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${CATEGORY_CONFIG[selectedItem.category]?.color}20` }}
                      >
                        {selectedItem.image_url ? (
                          <img src={selectedItem.image_url} alt={selectedItem.name} className="max-h-full max-w-full object-contain p-4" />
                        ) : (
                          <Package className="w-24 h-24" style={{ color: CATEGORY_CONFIG[selectedItem.category]?.color, opacity: 0.5 }} />
                        )}
                      </div>
                    </div>
                    
                    {/* Item Details */}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#D66238' }}>{selectedItem.name}</h3>
                      <p className="text-gray-600 mb-4">{selectedItem.description}</p>
                      
                      <div className="flex items-center gap-2 mb-6">
                        <Coins className="w-6 h-6" style={{ color: '#FFD700' }} />
                        <span className="text-2xl font-bold" style={{ color: '#D66238' }}>{selectedItem.price} coins</span>
                      </div>
                      
                      {/* Quantity Selector (for inventory items) */}
                      {selectedItem.item_type === 'inventory' && (
                        <div className="flex items-center gap-4 mb-6">
                          <span className="font-medium">Quantity:</span>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" onClick={() => setPurchaseQuantity(Math.max(1, purchaseQuantity - 1))} disabled={purchaseQuantity <= 1}>
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="text-xl font-bold w-12 text-center">{purchaseQuantity}</span>
                            <Button variant="outline" size="icon" onClick={() => setPurchaseQuantity(purchaseQuantity + 1)}>
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Total */}
                      <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: '#FFF8E1' }}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Total:</span>
                          <div className="flex items-center gap-2">
                            <Coins className="w-5 h-5" style={{ color: '#FFD700' }} />
                            <span className="text-xl font-bold" style={{ color: '#D66238' }}>
                              {(selectedItem.price * purchaseQuantity).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm mt-2" style={{ color: canAfford(selectedItem) ? '#228B22' : '#D66238' }}>
                          {canAfford(selectedItem) 
                            ? `You'll have ${(userCoins - selectedItem.price * purchaseQuantity).toLocaleString()} coins remaining`
                            : `You need ${(selectedItem.price * purchaseQuantity - userCoins).toLocaleString()} more coins`
                          }
                        </p>
                      </div>
                      
                      {/* Purchase Button */}
                      <Button
                        onClick={handlePurchase}
                        disabled={!canAfford(selectedItem) || purchasing}
                        className="w-full py-6 text-lg gap-2"
                        style={{ backgroundColor: canAfford(selectedItem) ? '#6FCFD3' : '#ccc', color: 'white' }}
                      >
                        {purchasing ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            Purchase
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Items Grid View */
                <div className="flex-1 overflow-hidden flex flex-col">
                  {/* Category Tabs */}
                  <div className="flex gap-2 p-4 overflow-x-auto" style={{ backgroundColor: '#f5f5f5' }}>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => setActiveCategory(key)}
                        className="px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all"
                        style={{
                          backgroundColor: activeCategory === key ? config.color : 'white',
                          color: activeCategory === key ? 'white' : '#372F2F',
                          border: `2px solid ${config.color}`
                        }}
                      >
                        {config.label} ({getItemsByCategory(key).length})
                      </button>
                    ))}
                  </div>
                  
                  {/* Items Grid */}
                  <div className="flex-1 overflow-y-auto p-4">
                    {getItemsByCategory(activeCategory).length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p>No items in this category</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {getItemsByCategory(activeCategory).map((item) => (
                          <Card 
                            key={item.id}
                            className="overflow-hidden cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                            style={{ border: `2px solid ${CATEGORY_CONFIG[activeCategory]?.color}` }}
                            onClick={() => setSelectedItem(item)}
                          >
                            <div 
                              className="aspect-square flex items-center justify-center"
                              style={{ backgroundColor: `${CATEGORY_CONFIG[activeCategory]?.color}15` }}
                            >
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="max-h-full max-w-full object-contain p-2" />
                              ) : (
                                <Package className="w-12 h-12" style={{ color: CATEGORY_CONFIG[activeCategory]?.color, opacity: 0.4 }} />
                              )}
                            </div>
                            <div className="p-3">
                              <h4 className="font-bold text-sm truncate" style={{ color: '#372F2F' }}>{item.name}</h4>
                              <div className="flex items-center gap-1 mt-1">
                                <Coins className="w-4 h-4" style={{ color: '#FFD700' }} />
                                <span className="font-bold" style={{ color: userCoins >= item.price ? '#D66238' : '#ccc' }}>
                                  {item.price}
                                </span>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Ranch;
